import DeclarationInput from 'components/ui/composed/declarations/formInput';
import DeclarationNumberInput from 'components/ui/composed/declarations/formInput/DeclarationNumberInput';
import DeclarationSelect from 'components/ui/composed/declarations/formSelect';
import { FormikProps, FormikProvider } from 'formik';
import { get } from 'lodash';
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ICS2CodelistsMap } from 'store/codelists/code';
import { DeclarationTypes, ICS2MessageTypes } from 'store/declarations/enums/common/declaration-types';
import addPathPrefix from 'utils/addPathPrefix';
import useHideEntities from 'hooks/useHideEntities';
import NewFormCard, { FormCardContainer } from 'views/declarations/common/cards/NewFormCard';
import MultipleItemsCard from 'views/declarations/common/MultipleItemsCard';
import { FormTabs } from 'views/declarations/declaration-tabs/declaration-form/DeclarationFormTab';
import { validators } from 'views/declarations/uk/export/validations/validations';
import { getFormikProps } from 'views/declarations/utils/form-utils';
import {
    AdditionalInformationCard,
    AdditionalSupplyChainActorCard,
    DangerousGoodsCard,
    PassiveBorderTransportMeansCard,
    SupportingDocumentsCard,
    TransportEquipmentCard,
} from './cards/SharedCards';

interface Props {
    formik: FormikProps<any>;
    viewOnly?: boolean | undefined;
}

const COMMODITY_CODE = 'commodity.commodityCode';

/**
 * The two commodity code fields sit in a `commodityCode` wrapper the message structure marks
 * optional. Writing either one on its own leaves the wrapper behind holding nothing once the user
 * clears the field, and the wrapper then reports the code it requires as missing - on a class the
 * message does not ask for, and with no way to clear it from the form. Writing the pair together
 * lets the wrapper go when both fields are empty.
 */
const onCommodityCodeChange =
    (formik: FormikProps<any>, field: 'harmonizedSystemSubHeadingCode' | 'combinedNomenclatureCode') =>
    (event: ChangeEvent<HTMLInputElement>) => {
        const next = { ...get(formik.values, COMMODITY_CODE), [field]: event.target.value || null };
        const isEmpty = !next.harmonizedSystemSubHeadingCode && !next.combinedNomenclatureCode;

        formik.setFieldValue(COMMODITY_CODE, isEmpty ? null : next, true);
    };

const ICS2ENSProductsForm = ({ formik, viewOnly }: Props) => {
    const { t } = useTranslation('form');
    const location = useLocation();
    const { isFormType } = useHideEntities();
    const isMasterItem = location.pathname.includes(FormTabs.MASTER_ITEMS);

    const isF11 = isFormType([ICS2MessageTypes.F11]);

    return (
        <FormikProvider value={formik}>
            <NewFormCard title={isMasterItem ? 'Master Item Details' : 'Goods Information'}>
                <FormCardContainer>
                    <DeclarationInput
                        refNumber="18 09 056 000"
                        label="Commodity Code"
                        condensed
                        {...getFormikProps('commodity.commodityCode.harmonizedSystemSubHeadingCode', formik)}
                        onChange={onCommodityCodeChange(formik, 'harmonizedSystemSubHeadingCode')}
                    />
                    <DeclarationInput
                        refNumber="18 09 057 000"
                        label="Combined Nomenclature Code"
                        condensed
                        maxLength={2}
                        {...getFormikProps('commodity.commodityCode.combinedNomenclatureCode', formik)}
                        onChange={onCommodityCodeChange(formik, 'combinedNomenclatureCode')}
                    />
                    <DeclarationInput
                        required
                        refNumber="18 05 001 000"
                        label="Description of Goods"
                        condensed
                        {...getFormikProps('commodity.descriptionOfGoods', formik)}
                    />
                    <DeclarationNumberInput
                        required
                        maxLength={16}
                        precision={6}
                        refNumber="18 04 001 000"
                        label="Gross Mass"
                        condensed
                        {...getFormikProps('weight.grossMass', formik)}
                    />
                    <DeclarationSelect
                        hideCodelistMenu
                        refNumber="18 08 001 000"
                        label="CUS Code"
                        condensed
                        codeValidation={[]}
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL719,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                        {...getFormikProps('commodity.cusCode', formik)}
                    />
                </FormCardContainer>
            </NewFormCard>

            <PassiveBorderTransportMeansCard formik={formik} hidden={isF11} viewOnly={viewOnly} />

            <DangerousGoodsCard formik={formik} viewOnly={viewOnly} />

            <MultipleItemsCard
                required
                viewOnly={viewOnly}
                path="packaging"
                initialValues={{ numberOfPackages: '', typeOfPackages: undefined, shippingMarks: '' }}
                list={(list) => [
                    {
                        field: t('packages.packageType'),
                        value: list.typeOfPackages,
                    },
                    { field: t('packages.numberOfPackages'), value: list.numberOfPackages },
                    { field: t('packages.shippingMarks'), value: list.shippingMarks },
                ]}
                condensed
                title="Packages"
            >
                {(path) => (
                    <FormCardContainer>
                        <DeclarationSelect
                            required
                            refNumber="18 06 003 000"
                            label={t('packages.packageType')}
                            codeValidation={[validators.maxLength(2)]}
                            condensed
                            codelistProps={{
                                codelistName: ICS2CodelistsMap.CL017,
                                declarationType: DeclarationTypes.ICS2,
                            }}
                            {...getFormikProps(addPathPrefix(path, 'typeOfPackages'), formik)}
                        />

                        <DeclarationNumberInput
                            refNumber="18 06 004 000"
                            maxLength={8}
                            precision={0}
                            integerNumbers
                            label={t('packages.numberOfPackages')}
                            condensed
                            {...getFormikProps(addPathPrefix(path, 'numberOfPackages'), formik)}
                        />

                        <DeclarationInput
                            maxLength={512}
                            refNumber="18 06 054 000"
                            label={t('packages.shippingMarks')}
                            condensed
                            {...getFormikProps(addPathPrefix(path, 'shippingMarks'), formik)}
                        />
                    </FormCardContainer>
                )}
            </MultipleItemsCard>

            <SupportingDocumentsCard formik={formik} viewOnly={viewOnly} />

            <TransportEquipmentCard formik={formik} viewOnly={viewOnly} />

            <AdditionalSupplyChainActorCard formik={formik} viewOnly={viewOnly} />

            <AdditionalInformationCard formik={formik} viewOnly={viewOnly} />
        </FormikProvider>
    );
};

export default ICS2ENSProductsForm;
